import * as React from "react";
import "./EmailCtl.scss";
import { ContactEmail } from "@testout/testout-commerce/models/ContactEmail";
import { EmailArray, Email } from "@testout/testout-commerce/models/Email";

interface IEmailProps {
    disabled?: boolean;
    value?: string;
    onChange?: (value: string) => void;
    placeholder?: string;
    className?: string;
    style?: React.CSSProperties;
    onSelect?: (value: string) => Promise<ContactEmail[]|null>;
    onSetErrorMessage?: (error: string) => void;
}

interface IEmailState {
    isOpen: boolean;
    inProgress: boolean;
    isValid: boolean;
    values: EmailArray;
    items: ContactEmail[];
    focusIndex: number;
    searchValue: string;
    error: string;
}


export default class EmailCtl extends React.PureComponent<IEmailProps, IEmailState> {
    constructor(props: IEmailProps) {
        super(props);
        this.state = { 
            isOpen: false, 
            inProgress: false,
            isValid: false,
            values: new EmailArray(),
            items: [],
            focusIndex: -1, 
            searchValue: "",
            error: ""
        };
    }

    _controlRef = React.createRef<HTMLDivElement>();
    _valueRef = React.createRef<HTMLInputElement>();
    _scrollerRef = React.createRef<HTMLDivElement>();
    _itemRef = React.createRef<HTMLLIElement>();
    _needFocus = false;

    static getDerivedStateFromProps(props: IEmailProps, state: IEmailState) {
        //console.log(`derive: props.value = ${props.value},  state.values = ${state.values.toString()}`);
        if ((props.value ?? "") === state.values.toString()) return null;
        try {
            return { values: new EmailArray(props.value) };
        } catch (e: any) {
            if (props?.onSetErrorMessage)
                props?.onSetErrorMessage(e.message);
        }
        return null;
    }

    onValuesClick = (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        this._needFocus = true;
        this.forceUpdate();
    }

    onSearchValueFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        e.stopPropagation();

        if (!this.state.isOpen)
            this.setState({ isOpen: true, searchValue: "", isValid: false, items: [], focusIndex: -1 });
    }

    _debounceId: number = 0;

    cancelSelect() {
        if (this._debounceId) window.clearTimeout(this._debounceId);
    }

    onSearchValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.cancelSelect();
        if (e.target.value) {
            const items = this.state.items.slice(this.state.isValid ? 1 : 0);
            const isValid = Email.isValid(e.target.value);
            if (isValid) items.unshift(new ContactEmail("", e.target.value));

            this.setState({ inProgress: true, searchValue: e.target.value, isValid: isValid, items: items, focusIndex: items.length > 0 ? 0 : -1 }, 
                () => this._debounceId = window.setTimeout(async () => {
                    try {
                        if (this.props.onSelect) {
                            const results = await this.props.onSelect(this.state.searchValue);
                            if (results) {
                                const newItems = this.state.items
                                    .slice(0, this.state.isValid ? 1 : 0)
                                    .concat(results.map((item: ContactEmail) => new ContactEmail(item.id,
                                        item.address,
                                        item.displayName,
                                        item.description)));
                                this.setState({ items: newItems });
                            }
                            this.setState({error: ""});
                        }
                    } catch (error: any) {
                        this.setState({ error: `Search failed. ${error.message}` });
                    } finally {
                        this.setState({ inProgress: false });
                    }
                }, 500));
        }
        else
            this.setState({ inProgress: false, searchValue: "", isValid: false, items: [], focusIndex: -1 });
    }

    onSearchValueKeyDown = (e: React.KeyboardEvent) => {
        if (this.props.disabled) return;
        if (e.key === "Enter") {
            if (this.state.focusIndex >= 0) {
                const values = new EmailArray(this.state.values.toString());
                values.add(this.state.items[this.state.focusIndex].toString());
                this.cancelSelect();
                this.setState({ inProgress: false, values: values, searchValue: "", isValid: false, items: [], focusIndex: -1 });
                this._needFocus = true;

                this.props.onChange?.(values.toString());
            }
            e.preventDefault();
        } else if (e.key === "," || e.key === ";" || e.key === "Tab") {
            if (this.state.searchValue && Email.isValid(this.state.searchValue)) {
                const values = new EmailArray(this.state.values.toString());
                values.add(this.state.searchValue);
                this.cancelSelect();
                this.setState({ inProgress: false, values: values, searchValue: "", isValid: false, items: [], focusIndex: -1 });

                this.props.onChange?.(values.toString());
            }
            if (e.key !== "Tab") e.preventDefault();
        } else if (e.key === "Backspace") {
            if (!this.state.searchValue && this.state.values.length > 0) {
                const values = new EmailArray(this.state.values.toString());
                values.remove(values.length - 1);
                this.setState({ values: values });

                this.props.onChange?.(values.toString());
                e.preventDefault();
            }
        } else if (e.key === "Escape") {
                this.setState({ searchValue: "", items: [], focusIndex: -1 });
            e.preventDefault();
        } else if (e.key === "ArrowDown") {
            if (this.state.focusIndex < this.state.items.length - 1)
                this.setState({ focusIndex: this.state.focusIndex + 1 });
            e.preventDefault();
        } else if (e.key === "ArrowUp") {
            if (this.state.focusIndex > 0) 
                this.setState({ focusIndex: this.state.focusIndex - 1 });
            e.preventDefault();
        }
    }

    onItemClick = (itemIndex: number, e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        this._needFocus = true;
        
        const values = new EmailArray(this.state.values.toString());
        values.add(this.state.items[itemIndex].toString());
        this.setState({ values: values, searchValue: "", isValid: false, items: [], focusIndex: -1 });

        this.props.onChange?.(values.toString());
    }

    onValueRemoveClick = (itemIndex: number, e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();

        const values = new EmailArray(this.state.values.toString());
        values.remove(itemIndex);
        this.setState({ values: values });
        this._needFocus = true;

        this.props.onChange?.(values.toString());
    }

    onClearAll = (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();

        const values = new EmailArray("");
        this.setState({ values: values });
        this._needFocus = true;

        this.props.onChange?.(values.toString());
    }

    onDocumentFocusIn = (e: Event) => {
        // Close the drop-down if focus/click outside of control
        const divControl = this._controlRef.current;
        if (divControl && !divControl.contains(e.target as Node) && this.state.isOpen && 
            (e.target as Node).getRootNode() === document) // Do not close if click was on node that has been removed from DOM tree
            this.setState({ isOpen: false });
    }

    positionDropDown() {
        const divControl: HTMLDivElement|null = this._controlRef.current;
        const divScroller: HTMLDivElement|null = this._scrollerRef.current;
        if (this.state.isOpen && 
            divControl && divScroller) {
            //const rect = divControl.getBoundingClientRect();
            //console.log(rect.toJSON());
            //divScroller.style.top = "10px";
            //divScroller.style.top = "10px";
            
        //    if (liItem.offsetTop < divScroller.scrollTop)
        //        divScroller.scrollTop = liItem.offsetTop;
        //    if (liItem.offsetTop + liItem.offsetHeight > divScroller.scrollTop + divScroller.clientHeight)
        //        divScroller.scrollTop = liItem.offsetTop + liItem.offsetHeight - divScroller.clientHeight;
        }
    }

    ensureItemVisible() {
        const liItem: HTMLLIElement|null = this._itemRef.current;
        const divScroller: HTMLDivElement|null = this._scrollerRef.current;
        if (this.state.isOpen && this.state.focusIndex >= 0 && 
            divScroller && liItem) {
            if (liItem.offsetTop < divScroller.scrollTop)
                divScroller.scrollTop = liItem.offsetTop;
            if (liItem.offsetTop + liItem.offsetHeight > divScroller.scrollTop + divScroller.clientHeight)
                divScroller.scrollTop = liItem.offsetTop + liItem.offsetHeight - divScroller.clientHeight;
        }
    }

    private _canvas: HTMLCanvasElement = document.createElement("canvas");
    setSearchValueWidth() {
        const inpValue = this._valueRef.current;
        if (inpValue) {
            const context = this._canvas.getContext("2d");
            if (context) {
                const style = window.getComputedStyle(inpValue);
                context.font = style.font;
                const width = Math.ceil(context.measureText(this.state.searchValue).width);
                inpValue.style.width = `${width + 2}px`;
            }
        }
    }

    componentDidMount() {
        document.addEventListener("focusin", this.onDocumentFocusIn);
        document.addEventListener("click", this.onDocumentFocusIn);
        this.positionDropDown();
        this.ensureItemVisible();
        this.setSearchValueWidth();
    }

    componentDidUpdate() {
        this.positionDropDown();
        this.ensureItemVisible();
        this.setSearchValueWidth();
        if (this._needFocus) {
            this._needFocus = false;
            const inpValue: HTMLInputElement | null = this._valueRef.current;

            if (inpValue) {

                inpValue.focus();
            }
        }
    }

    componentWillUnmount() {
        document.removeEventListener("focusin", this.onDocumentFocusIn);
        document.removeEventListener("click", this.onDocumentFocusIn);
    }

    render() {
        const { focusIndex, isOpen, inProgress, error, values, items, searchValue } = this.state;
        const { disabled, style, placeholder  } = this.props;

        //console.log(`render(${values.toString()})`);
        return <div className={`email${disabled ? " disabled" : ""}${!values.length ? " empty" : ""}`}
                    style={style} ref={this._controlRef}>
                   <div className={`values${!values.length ? " empty" : ""}${disabled ? " disabled" : ""}`} onClick={this.onValuesClick}>
                        {!values.length && !searchValue && 
                            <div>{placeholder}</div>}
                        {values.map((email: Email, i: number) => 
                            <div key={i}>
                                {email.displayName}
                                {email.displayName ? " <" : ""}<span>{email.address}</span>{email.displayName ? ">" : ""}
                                {!this.props.disabled &&
                                    <div className="x" title="Remove" onClick={(e: React.MouseEvent<HTMLDivElement>) => this.onValueRemoveClick(i, e)}/>}
                            </div>)}
                        {!this.props.disabled && 
                            <input type="input" key="search" ref={this._valueRef} title="Type to enter email"
                                value={this.state.searchValue} 
                                onChange={this.onSearchValueChange} 
                                onKeyDown={this.onSearchValueKeyDown}
                                onFocus={this.onSearchValueFocus} />}
                        {!!values.length && <div className="big-x" title="Remove All" onClick={this.onClearAll} />}
                   </div>
                   {isOpen &&
                       <div className="scroller" ref={this._scrollerRef}>
                           {error
                               ? <div key="msg" className="error">{error}</div>
                               : !searchValue
                               ? <div key="msg">Type to search...</div>
                               : items.length === 0
                               ? <div key="msg">Type to search or complete valid address...</div>
                               : !Email.isValid(searchValue)
                               ? <div key="msg">Select address, or type to search or complete valid address...</div>
                               : undefined }
                           {inProgress && <div key="spinner" className="spinner"></div>}

                           {items.length > 0 && <ul>
                               {items.map((email: ContactEmail, i) =>
                                   <li key={i} ref={i === focusIndex ? this._itemRef : undefined}
                                       className={`${i === focusIndex ? "focus" : ""} ${(email.description ?? "").startsWith("*") ? "prefer" : ""}`}
                                       onClick={(e: React.MouseEvent<HTMLLIElement>) => this.onItemClick(i, e) }>
                                       
                                       {email.displayName}
                                       <span>{email.displayName ? `<${email.address}>` : email.address}</span>
                                       {((email.description ?? "").startsWith("*") ? (email.description ?? "").substr(1) : email.description) && 
                                           <span>{(email.description ?? "").startsWith("*") ? (email.description ?? "").substr(1) : email.description}</span>}
                                   </li>)}
                           </ul>}
                       </div>}
               </div>;
    }
}